import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Briefcase, Lock, Star, FileSignature, Eye, Calendar, Users, BadgeCheck, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, fmtFJD, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ShareButton from "@/components/ShareButton";
import ServiceConversionSidebar from "@/components/ServiceConversionSidebar";
import { INDUSTRY_BY_ID } from "@/lib/businesses";

const FALLBACK = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&q=85";

export default function BusinessDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [biz, setBiz] = useState(null);
    const [error, setError] = useState(null);
    const [financials, setFinancials] = useState(null);
    const [ndaRequest, setNdaRequest] = useState(null); // existing request status
    const [watchlisted, setWatchlisted] = useState(false);
    const [showNdaModal, setShowNdaModal] = useState(false);
    const [ndaForm, setNdaForm] = useState({ buyer_full_name: "", buyer_company: "", purpose: "" });
    const [submitting, setSubmitting] = useState(false);

    const reload = () => {
        api.get(`/businesses/${id}`).then(({ data }) => setBiz(data)).catch((e) => setError(e.response?.data?.detail || "Not found"));
        if (user) {
            // try to load gated financials — succeeds only if approved or owner
            api.get(`/businesses/${id}/financials`).then(({ data }) => setFinancials(data)).catch(() => setFinancials(null));
            // load NDA status
            api.get("/my/nda-requests").then(({ data }) => {
                const mine = (data.items || []).find((r) => r.business_id === id);
                setNdaRequest(mine || null);
            }).catch(() => {});
            // load watchlist status
            api.get("/my/business-watchlist").then(({ data }) => {
                setWatchlisted((data.items || []).some((b) => b.id === id));
            }).catch(() => {});
        }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id, user?.id]);

    const submitNda = async () => {
        if (!ndaForm.buyer_full_name || !ndaForm.purpose) {
            toast.error("Please fill in your full name and purpose.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/businesses/${id}/nda-request`, ndaForm);
            toast.success("NDA request sent. The seller will be notified.");
            setShowNdaModal(false);
            reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const toggleWatch = async () => {
        if (!user) {
            navigate("/login");
            return;
        }
        try {
            if (watchlisted) {
                await api.delete(`/businesses/${id}/watchlist`);
                setWatchlisted(false);
                toast.success("Removed from watchlist.");
            } else {
                await api.post(`/businesses/${id}/watchlist`);
                setWatchlisted(true);
                toast.success("Added to watchlist.");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    if (error) return (
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
            <h2 className="font-display text-2xl font-bold mb-2">Business not found</h2>
            <Button asChild variant="outline"><Link to="/businesses"><ArrowLeft className="h-4 w-4 mr-1" /> All businesses</Link></Button>
        </div>
    );
    if (!biz) return null;

    const industry = INDUSTRY_BY_ID[biz.industry];
    const Icon = industry?.icon || Briefcase;
    const isOwner = user?.id === biz.owner_id;
    const photo = biz.external_photos?.[0] || industry?.fallback_photo || FALLBACK;

    return (
        <div data-testid="business-detail-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <Button variant="ghost" size="sm" onClick={() => navigate("/businesses")} className="mb-4">
                <ArrowLeft className="h-4 w-4 mr-1" /> All businesses
            </Button>

            <div className="aspect-[16/8] rounded-xl overflow-hidden mb-8 relative">
                <img src={photo} alt={biz.title} className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                    <Badge className="bg-card/90 backdrop-blur text-foreground border-0 font-app text-[11px] uppercase tracking-wider flex items-center gap-1">
                        <Icon className="h-3 w-3" /> {industry?.label || biz.industry}
                    </Badge>
                    {biz.real_estate_included && <Badge className="bg-fiji-green text-white border-0 font-app text-[11px] uppercase tracking-wider">+Real Estate</Badge>}
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground font-app mb-2">
                            <MapPin className="h-3 w-3" /> {biz.location_region}, {biz.location_island}
                        </div>
                        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                            <h1 className="font-display text-4xl font-bold flex-1">{biz.title}</h1>
                            <ShareButton type="business" id={biz.id} title={biz.title} />
                        </div>
                        <p className="font-app text-base text-muted-foreground whitespace-pre-line">{biz.public_description}</p>
                    </div>

                    {/* NDA-gated section */}
                    <Card className={`p-5 ${financials ? "" : "bg-secondary/40 border-dashed"}`} data-testid="financials-section">
                        <div className="flex items-center gap-2 mb-3">
                            {financials ? <BadgeCheck className="h-5 w-5 text-fiji-green" /> : <Lock className="h-5 w-5 text-muted-foreground" />}
                            <h2 className="font-display text-xl font-bold">Financials & full pitch</h2>
                            {financials ? (
                                <Badge className="bg-fiji-green/15 text-fiji-green border-0 text-[10px] uppercase font-app">NDA cleared</Badge>
                            ) : (
                                <Badge className="bg-secondary text-muted-foreground border-0 text-[10px] uppercase font-app">NDA required</Badge>
                            )}
                        </div>
                        {financials ? (
                            <div data-testid="financials-revealed">
                                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                                    {financials.annual_revenue_fjd != null && (
                                        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Annual revenue</div><div className="font-display text-2xl font-bold tabular-nums">{fmtFJD(financials.annual_revenue_fjd)}</div></div>
                                    )}
                                    {financials.annual_ebitda_fjd != null && (
                                        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Annual EBITDA</div><div className="font-display text-2xl font-bold tabular-nums text-fiji-green">{fmtFJD(financials.annual_ebitda_fjd)}</div></div>
                                    )}
                                    {financials.year_established && (
                                        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Established</div><div className="font-display text-base font-medium flex items-center gap-1"><Calendar className="h-4 w-4" />{financials.year_established}</div></div>
                                    )}
                                    {financials.employees && (
                                        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Staff</div><div className="font-display text-base font-medium flex items-center gap-1"><Users className="h-4 w-4" />{financials.employees}</div></div>
                                    )}
                                    {financials.inventory_value_fjd != null && (
                                        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Inventory value</div><div className="font-display text-base font-medium tabular-nums">{fmtFJD(financials.inventory_value_fjd)}</div></div>
                                    )}
                                </div>
                                {financials.private_description && (
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app mb-1">Full pitch</div>
                                        <p className="font-app text-sm whitespace-pre-line">{financials.private_description}</p>
                                    </div>
                                )}
                                {financials.reason_for_sale && (
                                    <div className="mt-3">
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app mb-1">Reason for sale</div>
                                        <p className="font-app text-sm">{financials.reason_for_sale}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                <p className="font-app text-sm text-muted-foreground mb-3">
                                    Revenue, EBITDA, year established, full pitch, and reason for sale are confidential.
                                    Sign the NDA to request access — the seller will review and approve.
                                </p>
                                {biz.revenue_range && (
                                    <p className="font-app text-xs text-muted-foreground mb-4">
                                        <span className="font-semibold">Revenue range hint:</span> {biz.revenue_range}
                                    </p>
                                )}
                                {!user && (
                                    <Button asChild data-testid="nda-login-cta"><Link to="/login">Sign in to request NDA</Link></Button>
                                )}
                                {user && !ndaRequest && !isOwner && (
                                    <Button onClick={() => setShowNdaModal(true)} data-testid="open-nda-btn" className="bg-coral text-white hover:opacity-90">
                                        <FileSignature className="h-4 w-4 mr-1" /> Request NDA & financials
                                    </Button>
                                )}
                                {user && ndaRequest && (
                                    <div data-testid="nda-status">
                                        <Badge className={
                                            ndaRequest.status === "pending" ? "bg-coral/15 text-coral border-0 font-app text-xs uppercase" :
                                            ndaRequest.status === "approved" ? "bg-fiji-green/15 text-fiji-green border-0 font-app text-xs uppercase" :
                                            "bg-destructive/15 text-destructive border-0 font-app text-xs uppercase"
                                        }>
                                            NDA {ndaRequest.status}
                                        </Badge>
                                        {ndaRequest.status === "pending" && <p className="text-xs font-app text-muted-foreground mt-2">Waiting on the seller — we'll notify you when they decide.</p>}
                                        {ndaRequest.status === "rejected" && ndaRequest.note && <p className="text-xs font-app text-muted-foreground mt-2">Seller note: "{ndaRequest.note}"</p>}
                                    </div>
                                )}
                                {isOwner && <p className="text-xs font-app text-muted-foreground">You're the owner — your financials are always visible to you below.</p>}
                            </div>
                        )}
                    </Card>
                </div>

                <aside className="space-y-4">
                    <Card className="p-5">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Asking price</div>
                        <div className="font-display text-3xl font-bold tabular-nums text-ocean dark:text-turquoise mt-1">{fmtFJD(biz.asking_price_fjd)}</div>
                        <div className="grid grid-cols-2 gap-2 mt-4 text-xs font-app text-muted-foreground">
                            <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {biz.views || 0} views</span>
                            <span className="flex items-center gap-1"><Star className="h-3 w-3" /> {biz.watchlist_count || 0} watching</span>
                        </div>
                        <Button onClick={toggleWatch} variant={watchlisted ? "default" : "outline"} className={`w-full mt-4 ${watchlisted ? "bg-coral text-white" : ""}`} data-testid="watchlist-toggle">
                            <Star className={`h-4 w-4 mr-1 ${watchlisted ? "fill-white" : ""}`} /> {watchlisted ? "On your watchlist" : "Add to watchlist"}
                        </Button>
                    </Card>

                    <ServiceConversionSidebar
                        context={{
                            kind: "business",
                            priceFjd: biz.asking_price_fjd,
                            zoning: biz.industry,
                        }}
                        listingTitle={biz.title}
                        listingUrl={typeof window !== "undefined" ? window.location.href : ""}
                    />
                </aside>
            </div>

            <Dialog open={showNdaModal} onOpenChange={setShowNdaModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Request NDA — {biz.title}</DialogTitle>
                        <DialogDescription className="font-app">
                            By submitting, you agree to keep all disclosed financials confidential. The seller reviews each request manually.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 my-2">
                        <div>
                            <Label>Your full legal name *</Label>
                            <Input data-testid="nda-name" value={ndaForm.buyer_full_name} onChange={(e) => setNdaForm((f) => ({ ...f, buyer_full_name: e.target.value }))} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Company / vehicle (optional)</Label>
                            <Input data-testid="nda-company" value={ndaForm.buyer_company} onChange={(e) => setNdaForm((f) => ({ ...f, buyer_company: e.target.value }))} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Purpose / serious-buyer note *</Label>
                            <Textarea data-testid="nda-purpose" value={ndaForm.purpose} onChange={(e) => setNdaForm((f) => ({ ...f, purpose: e.target.value }))} placeholder="Active buyer with FJ$1M cash, 30-day diligence target." rows={3} className="mt-1.5" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNdaModal(false)}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                        <Button onClick={submitNda} disabled={submitting} className="bg-coral text-white" data-testid="nda-submit-btn">
                            <FileSignature className="h-4 w-4 mr-1" /> {submitting ? "Sending…" : "Sign & request"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
